import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { api } from '../../services/api'
import './Catalog.css'
import { MainLayout } from '../../Layout/MainLayout'

interface Course {
    id: string
    title: string
    description: string
    area: { name: string }
    estimatedDurationMin: number
    mandatory: boolean
    urlCover: string
    status: string
    progress?: number
    completed?: boolean
}

export function Catalog() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [courses, setCourses] = useState<Course[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.get('/course?status=publicado')
            .then(async r => {
                const coursesData: Course[] = r.data.data

                // Busca progresso de cada curso
                const progressList = await Promise.all(
                    coursesData.map(c =>
                        api.get(`/progress/course/${c.id}`)
                            .then(res => ({ courseId: c.id, ...res.data }))
                            .catch(() => ({ courseId: c.id, percentComplete: 0, completedVideos: 0, totalVideos: 0 }))
                    )
                )
                // Cruza os dados
                const enriched = coursesData.map(c => {
                    const p = progressList.find(p => p.courseId === c.id)
                    return {
                        ...c,
                        progress: p?.percentComplete ?? 0,
                        completed: p?.percentComplete === 100,
                    }
                })

                setCourses(enriched)
            })
            .catch(() => setCourses([]))
            .finally(() => setLoading(false))
    }, [])

    const required = courses.filter(c => c.mandatory)
    const optional = courses.filter(c => !c.mandatory)
    const doneCount = courses.filter(c => c.completed).length
    const totalCount = courses.length
    const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0
    const inProgress = courses.find(c => (c.progress ?? 0) > 0 && !c.completed)
    function getBadge(c: Course) {
        if (c.completed) return { cls: 'badge-done', label: 'Concluído' }
        if ((c.progress ?? 0) > 0) return { cls: 'badge-progress', label: 'Em andamento' }
        if (c.mandatory) return { cls: 'badge-required', label: 'Obrigatório' }
        return null
    }

    if (loading) return (
        <div className="container fade-in" style={{ paddingTop: 60, color: 'var(--text-3)', textAlign: 'center' }}>
            Carregando cursos...
        </div>
    )

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="page-head">
                    <div>
                        <div className="kicker">Sua trilha</div>
                        <h1 className="serif">Biblioteca</h1>
                    </div>
                    <div className="meta">
                        <strong>{pct}%</strong>
                        {doneCount} de {totalCount} cursos concluídos
                    </div>
                </div>

                {inProgress && (
                    <div className="resume-hero">
                        <div>
                            <div className="label">Continuar de onde parou</div>
                            <h2 className="serif">{inProgress.title}</h2>
                            <p className="desc">{inProgress.description}</p>
                            <div className="resume-progress">
                                <div className="progress-bar">
                                    <div style={{ width: `${inProgress.progress}%` }} />
                                </div>
                                <span style={{ color: 'var(--text-3)', fontSize: 13 }}>{inProgress.progress}%</span>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => navigate(`/player/${inProgress.id}`)}
                                >
                                    Retomar →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {required.length > 0 && (
                    <div className="section">
                        <div className="section-head">
                            <h3 className="serif">Obrigatórios</h3>
                            <span className="count">{required.length} cursos</span>
                        </div>
                        <div className="courses-grid">
                            {required.map(c => {
                                const badge = getBadge(c)
                                return (
                                    <div key={c.id} className="course-card" onClick={() => navigate(`/player/${c.id}`)}>
                                        <div className="course-thumb">
                                            {c.urlCover
                                                ? <img src={c.urlCover} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <span className="thumb-icon">A</span>
                                            }
                                            {badge && <span className={`course-badge ${badge.cls}`}>{badge.label}</span>}
                                        </div>
                                        <div className="course-body">
                                            <div className="course-area">{c.area?.name}</div>
                                            <div className="course-title serif">{c.title}</div>
                                            <div className="course-desc">{c.description}</div>
                                            <div className="course-meta">
                                                <span>{c.estimatedDurationMin} min</span>
                                                <span className="dot" />
                                                <div className="progress-mini">
                                                    <div style={{ width: `${c.progress ?? 0}%` }} />
                                                </div>
                                                <span>{c.progress ?? 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {optional.length > 0 && (
                    <div className="section">
                        <div className="section-head">
                            <h3 className="serif">Opcionais</h3>
                            <span className="count">{optional.length} cursos</span>
                        </div>
                        <div className="courses-grid">
                            {optional.map(c => {
                                const badge = getBadge(c)
                                return (
                                    <div key={c.id} className="course-card" onClick={() => navigate(`/player/${c.id}`)}>
                                        <div className="course-thumb">
                                            {c.urlCover
                                                ? <img src={c.urlCover} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <span className="thumb-icon">A</span>
                                            }
                                            {badge && <span className={`course-badge ${badge.cls}`}>{badge.label}</span>}
                                        </div>
                                        <div className="course-body">
                                            <div className="course-area">{c.area?.name}</div>
                                            <div className="course-title serif">{c.title}</div>
                                            <div className="course-desc">{c.description}</div>
                                            <div className="course-meta">
                                                <span>{c.estimatedDurationMin} min</span>
                                                <span className="dot" />
                                                <div className="progress-mini">
                                                    <div style={{ width: `${c.progress ?? 0}%` }} />
                                                </div>
                                                <span>{c.progress ?? 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    )
}